import { USE_MOCK_API } from "@/mocks/guard";
import { mockApi } from "@/mocks/mockApiAdapter";
import type { ApiEnvelope, ApiSuccess, ListParams } from "./types";
import { ApiError, AuthError, ForbiddenError, NetworkError, ValidationError } from "./errors";

const BASE_URL = normaliseBaseUrl(import.meta.env.VITE_API_BASE_URL);

/**
 * Normalise the base URL so callers can safely concatenate `/path` values.
 * Enforces the "/api included exactly once" rule from the contract.
 */
function normaliseBaseUrl(raw: string | undefined): string {
  if (!raw) {
    if (USE_MOCK_API) return "";
    throw new Error(
      "[api] VITE_API_BASE_URL is not set. Copy .env.example to .env and configure it.",
    );
  }
  const trimmed = raw.replace(/\/+$/, "");
  if (!/\/api$/.test(trimmed)) {
    // Fail loudly at startup instead of silently double-prefixing.
    throw new Error(
      `[api] VITE_API_BASE_URL must end with "/api" (got: "${raw}"). ` +
        `Example: https://sowetosuperunitedfc.com/api`,
    );
  }
  return trimmed;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: ListParams | Record<string, unknown>;
  signal?: AbortSignal;
  /** multipart/form-data body. Overrides `body`. */
  formData?: FormData;
  /** Include CSRF header on unsafe methods. Default: true for non-GET. */
  csrf?: boolean;
  headers?: Record<string, string>;
}

/**
 * The single fetch wrapper. Every service must go through this function.
 * - Always sends credentials so PHP's HttpOnly session cookie is included.
 * - Attaches an X-XSRF-TOKEN header on unsafe methods, read from a
 *   non-HttpOnly cookie that PHP is expected to issue at /auth/csrf.
 * - Never reads or writes the session cookie from JS.
 * - Normalises the PHP envelope into typed data or a typed error.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (USE_MOCK_API) {
    return mockApi<T>(path, options);
  }

  const method = options.method ?? "GET";
  const url = buildUrl(path, options.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...options.headers,
  };
  let body: BodyInit | undefined;
  if (options.formData) {
    body = options.formData;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  const needsCsrf = options.csrf ?? method !== "GET";
  if (needsCsrf) {
    const token = readCookie("XSRF-TOKEN");
    if (token) headers["X-XSRF-TOKEN"] = decodeURIComponent(token);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      credentials: "include",
      headers,
      body,
      signal: options.signal,
    });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new NetworkError();
  }

  // Laravel returns 419 when the session-bound CSRF token has expired. Prime a
  // fresh token and retry the unsafe request once; never fall back to mock data.
  if (response.status === 419 && needsCsrf) {
    await primeCsrf();
    const refreshedToken = readCookie("XSRF-TOKEN");
    if (refreshedToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(refreshedToken);
    try {
      response = await fetch(url, {
        method,
        credentials: "include",
        headers,
        body,
        signal: options.signal,
      });
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err;
      throw new NetworkError();
    }
  }

  return parseEnvelope<T>(response);
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(
    `${BASE_URL}${path.startsWith("/") ? path : `/${path}`}`,
    typeof window === "undefined" ? "http://placeholder" : window.location.origin,
  );
  if (query) {
    const { filters, ...rest } = query as ListParams & Record<string, unknown>;
    for (const [k, v] of Object.entries(rest)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
    if (filters && typeof filters === "object") {
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== "")
          url.searchParams.set(`filter[${k}]`, String(v));
      }
    }
  }
  return url.toString();
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let payload: ApiEnvelope<T> | undefined;
  try {
    payload = (await response.json()) as ApiEnvelope<T>;
  } catch {
    // Non-JSON error (HTML error page, gateway timeout, etc.)
    if (!response.ok) {
      throw new ApiError(`Request failed with status ${response.status}.`, response.status);
    }
    return undefined as T;
  }

  if (payload && "success" in payload && payload.success) {
    const success = payload as ApiSuccess<T>;
    // Include meta as a non-enumerable field so paginated readers can access it.
    return success.data;
  }

  const failure = payload as ApiFailureLike;
  const message = failure?.message ?? `Request failed with status ${response.status}.`;
  if (response.status === 401) throw new AuthError(message, failure);
  if (response.status === 403) throw new ForbiddenError(message, failure);
  if (response.status === 422 && failure?.errors)
    throw new ValidationError(message, failure.errors, failure);
  throw new ApiError(message, response.status, failure?.code, failure);
}

type ApiFailureLike = {
  message?: string;
  code?: string;
  errors?: Record<string, string[]>;
};

/**
 * Fetch a paginated list. Returns both data and pagination meta.
 */
export async function apiPaginated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T[]; meta: import("./types").PaginationMeta }> {
  if (USE_MOCK_API) {
    return mockApi<{ data: T[]; meta: import("./types").PaginationMeta }>(path, options);
  }
  const method = options.method ?? "GET";
  const url = buildUrl(path, options.query);
  const response = await fetch(url, {
    method,
    credentials: "include",
    headers: { Accept: "application/json" },
    signal: options.signal,
  });
  const payload = (await response.json()) as {
    success: boolean;
    message?: string;
    data: T[];
    meta: import("./types").PaginationMeta;
    errors?: Record<string, string[]>;
  };
  if (!payload.success) {
    throw new ApiError(payload.message ?? "Request failed.", response.status, undefined, payload);
  }
  return { data: payload.data, meta: payload.meta };
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? match[1] : null;
}

/**
 * Prime the CSRF cookie before performing unsafe requests.
 * PHP is expected to Set-Cookie: XSRF-TOKEN=... (SameSite=Lax, Secure, NOT HttpOnly)
 * on GET /auth/csrf.
 */
export async function primeCsrf(): Promise<void> {
  if (USE_MOCK_API) return;
  try {
    await fetch(`${BASE_URL}/auth/csrf`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
  } catch {
    // Non-fatal — the next request will surface the error.
  }
}
